import { styled } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';

import { BackIcon } from '../../../icons';
import { IconButton } from '../../atoms/IconButton';

type Props = {
  /** Rendered as the panel's `h2`. */
  title: string;
  /** State next to the title, typically a `Tag variantIcon="state"`. */
  status?: ReactNode;
  subtitle?: ReactNode;
  /** The panel's main action, e.g. a `contained small` button. */
  action?: ReactNode;
  /**
   * Back link to the list of a `ListDetailLayout`. Only shown below `md`, where the list and the
   * detail are not displayed together.
   */
  backHref?: string;
  onBackClick?: (event: MouseEvent<HTMLElement>) => void;
  backLabel?: string;
  'data-testid'?: string;
};

const DEFAULT_BACK_LABEL = 'Retour à la liste';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  [theme.breakpoints.up('md')]: {
    paddingInline: theme.spacing(3),
  },
}));

const Main = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
}));

const BackSlot = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginLeft: theme.spacing(-1),
  marginTop: theme.spacing(-0.5),
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const Texts = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

const TitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.5),
}));

const Title = styled('h2')(({ theme }) => ({
  ...theme.typography.h4,
  margin: 0,
  minWidth: 0,
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
  textWrap: 'balance',
}));

const Subtitle = styled('p')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  color: theme.palette.text.secondary,
  fontVariantNumeric: 'tabular-nums',
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

/**
 * Header of a detail `ListPanel` (its `header` slot): the picked item's title, state and summary
 * with the panel's main action. Below `md` it leads with a back link to the list.
 */
export function ListPanelHeader(props: Props) {
  return (
    <Root data-testid={props['data-testid'] ?? 'list-panel-header'}>
      <Main>
        {props.backHref ? (
          <BackSlot>
            <IconButton
              href={props.backHref}
              onClick={props.onBackClick}
              tooltip={props.backLabel ?? DEFAULT_BACK_LABEL}
            >
              <BackIcon />
            </IconButton>
          </BackSlot>
        ) : null}
        <Texts>
          <TitleRow>
            <Title>{props.title}</Title>
            {props.status}
          </TitleRow>
          {props.subtitle ? <Subtitle>{props.subtitle}</Subtitle> : null}
        </Texts>
      </Main>
      {props.action ? <Actions>{props.action}</Actions> : null}
    </Root>
  );
}

export type { Props as ListPanelHeaderProps };
